'use client';

import { ReactNode, useCallback, useLayoutEffect, useRef } from 'react';

interface VirtualScrollbarProps {
  children: ReactNode;
  className?: string;
}

export default function VirtualScrollbar({ children, className = '' }: VirtualScrollbarProps) {
  const viewportRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const thumbRef = useRef<HTMLDivElement>(null);
  const animationFrameRef = useRef<number | null>(null);

  const updateThumb = useCallback(() => {
    if (animationFrameRef.current !== null) return;

    animationFrameRef.current = requestAnimationFrame(() => {
      animationFrameRef.current = null;

      const viewport = viewportRef.current;
      const track = trackRef.current;
      const thumb = thumbRef.current;
      if (!viewport || !track || !thumb) return;

      const viewportHeight = viewport.clientHeight;
      const contentHeight = viewport.scrollHeight;
      const trackHeight = track.clientHeight;
      const isScrollable = contentHeight > viewportHeight && trackHeight > 0;

      thumb.hidden = !isScrollable;
      if (!isScrollable) return;

      const thumbHeight = (viewportHeight / contentHeight) * trackHeight;
      const maxScrollTop = contentHeight - viewportHeight;
      const maxThumbTop = trackHeight - thumbHeight;
      const thumbTop = (viewport.scrollTop / maxScrollTop) * maxThumbTop;

      thumb.style.height = `${thumbHeight}px`;
      thumb.style.transform = `translateY(${thumbTop}px)`;
    });
  }, []);

  useLayoutEffect(() => {
    const viewport = viewportRef.current;
    const content = contentRef.current;
    if (!viewport || !content) return;

    const resizeObserver = new ResizeObserver(updateThumb);
    resizeObserver.observe(viewport);
    resizeObserver.observe(content);
    updateThumb();

    return () => {
      resizeObserver.disconnect();
      if (animationFrameRef.current !== null) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
    };
  }, [updateThumb]);

  return (
    <div className='relative min-h-0 flex-1'>
      <div ref={viewportRef} className={`h-full overflow-y-auto scrollbar-hide ${className}`} onScroll={updateThumb}>
        <div ref={contentRef}>{children}</div>
      </div>

      <div
        ref={trackRef}
        aria-hidden='true'
        className='pointer-events-none absolute bottom-20pxr right-4pxr top-20pxr w-6pxr'
      >
        <div
          ref={thumbRef}
          className='absolute right-0 top-0 w-6pxr rounded-10pxr bg-gray-300 will-change-transform'
          hidden
        />
      </div>
    </div>
  );
}
